import {
    type BillActivationStatus,
    BillCategory,
    type BillCreationType,
    BillingCadence,
    type Dollars,
} from '@fruitful/types';
import { type IconType, tokens } from 'smoothie';

export {
    BillActivationStatus,
    BillCategory,
    BillCategoryNames,
    BillCategoryPrecedence,
    BillCreationType,
    BillingCadence,
} from '@fruitful/types';

export enum BillTransactionChangeMethod {
    MANUAL_MEMBER = 'manual_member',
    MANUAL_ADMIN = 'manual_admin',
    AUTO_ALGORITHMIC = 'auto_algorithmic',
    AUTO_LLM = 'auto_llm',
}

export enum BillTransactionStatus {
    ACTIVE = 'active',
    HIDDEN = 'hidden',
    REVIEW = 'review',
}

export interface BillTransaction {
    transactionId: string;
    dateAdded: Date;
    dateChanged: Date;
    addedBy: BillTransactionChangeMethod;
    hiddenBy: BillTransactionChangeMethod;
    status: BillTransactionStatus;
    transactionAmount: number;
    transactionDate: string;
    vendor: string;
    accountMask?: string;
    institutionName?: string;
}

export interface BillProposedChanges {
    transactionIdToAdd: string;
    accountId: string;
    latestTransactionId: string;
    frequency: string;
    expectedDay: string;
    averageAmount: number;
}

export interface _Bill {
    id: string;
    aiBillId: string;
    membershipId: string;
    accountId: string;
    name: string;
    displayName: string;
    payee: string;
    category: BillCategory;
    description: string;
    amount: Dollars; // Note: Dollars here, but Cents on server and db
    monthlyAmount: Dollars;
    cadence: BillingCadence;
    timingLabel: string;
    timingDescription: string;
    creationType: BillCreationType;
    createdAt: Date;
    activationStatus: BillActivationStatus;
    removedAt: Date | null;
    transactions: BillTransaction[];
    proposedChanges: BillProposedChanges | undefined;
    lastPaidAt: Date | null;
    memberMarkedAsSwitched: boolean | null;

    // Temporary, for the UI
    // Solution to be finalized with ENG-1616
    icon: IconType | null;
    color: string | null;
}

export type IBill = Required<_Bill>;
export type PartialBill = Partial<_Bill>;
export type ManualBill = Partial<
    Omit<_Bill, 'timingDescription' | 'timingLabel'> & {
        lastPaidAt: Date | undefined;
    }
>;

export interface SelectableBill extends IBill {
    isSelected: boolean;
}

export const BillCategoryBackgroundColors: Record<BillCategory, string> = {
    [BillCategory.CHILDCARE]: tokens.color.light.peach.peach300,
    [BillCategory.INSURANCE]: tokens.color.light.tomatoRed.tomatoRed300,
    [BillCategory.LOANS]: tokens.color.light.dropletBlue.dropletBlue300,
    [BillCategory.OTHER]: tokens.color.light.gray.gray300,
    [BillCategory.SUBSCRIPTIONS]: tokens.color.light.marigold.marigold300,
    [BillCategory.RENT_MORTGAGE]: tokens.color.light.wasabi.wasabi300,
    [BillCategory.UTILITIES]: tokens.color.light.sweetViolet.sweetViolet300,
    [BillCategory.CREDIT_CARD_MINIMUM]: tokens.color.light.gray.gray400,
};

export const BillCategoryTextColors: Record<BillCategory, string> = {
    [BillCategory.CHILDCARE]: tokens.color.light.peach.peach1200,
    [BillCategory.INSURANCE]: tokens.color.light.tomatoRed.tomatoRed1200,
    [BillCategory.LOANS]: tokens.color.light.dropletBlue.dropletBlue1200,
    [BillCategory.OTHER]: tokens.color.light.gray.gray1200,
    [BillCategory.SUBSCRIPTIONS]: tokens.color.light.marigold.marigold1200,
    [BillCategory.RENT_MORTGAGE]: tokens.color.light.wasabi.wasabi1200,
    [BillCategory.UTILITIES]: tokens.color.light.sweetViolet.sweetViolet1200,
    [BillCategory.CREDIT_CARD_MINIMUM]: tokens.color.light.gray.gray1200,
};

export enum InlineEditableBillFields {
    PAYEE = 'Payee',
    NICKNAME = 'Nickname (optional)',
    AMOUNT = 'Amount',
    PAID_FROM = 'Paid from',
    FREQUENCY = 'Frequency',
}

export const AiToPostgresBillingCadence: Record<string, BillingCadence> = {
    monthly: BillingCadence.MONTHLY,
    quarterly: BillingCadence.QUARTERLY,
    daily: BillingCadence.DAILY,
    weekly: BillingCadence.WEEKLY,
    'bi-weekly': BillingCadence.BI_WEEKLY,
    'semi-monthly': BillingCadence.SEMI_MONTHLY,
    'semi-annually': BillingCadence.SEMI_ANNUALLY,
    annually: BillingCadence.ANNUALLY,
    unknown: BillingCadence.UNKNOWN,
};

export enum EditProposedChangesRequestAction {
    APPROVE = 'approve',
    REJECT = 'reject',
}

export enum BillEditTypeParam {
    FREQUENCY = 'frequency',
    LAST_PAYMENT_DATE = 'lastPaymentDate',
    ACCOUNT = 'account',
    CATEGORY = 'category',
}

export enum BillContext {
    REVIEW = 'review',
    MANUAL = 'manual',
    DEFAULT = 'default',
    ONBOARDING = 'onboarding',
}
